/**
 * Admin Portal → Partners: the view logic, kept pure so jest can pin it.
 *
 * Every rule here mirrors a backend rule (routers/admin_partners.py,
 * routers/admin_partner_statements.py, services/partner_codes.py). The screen checks
 * first so an admin sees the reason before a round-trip; the server stays the judge
 * and its own words are always shown when it refuses.
 *
 * Spec: docs/handoff/PARTNERS_ADMIN_FE_HANDOFF.md, docs/plans/PARTNER_CODES_PLATFORM_DESIGN.md §8,
 * owner rulings docs/plans/WAVE8_SFD_OWNER_DECISIONS_2026_09_26.md (16–18b: typed offers,
 * trial 7/14/30 only, per-period discounts and bounties; an ENDED partner is final).
 */

import { DEFAULT_MONTHLY_TERM, monthlyTermChoices, type DiscountOffer } from '../../../components/admin/discountTermsLogic';

export const PARTNERS_API = '/api/admin/partners';
export const STATEMENTS_API = '/api/admin/partner-statements';
export const SITE_BASE = 'https://coachcedar.com';

// ── types (the shapes the endpoints answer) ───────────────────────────────────

export type PartnerStatus = 'active' | 'paused' | 'ended';
export type StatementStatus = 'draft' | 'approved' | 'sent' | 'paid' | 'void';
export type CodeState = 'active' | 'expired' | 'used_up' | 'redeemed' | 'revoked' | 'replaced';
export type CodeKind = 'shared' | 'single_use';

export interface PartnerRow {
  id: string;
  code: string;
  display_name: string;
  legal_name: string;
  status: PartnerStatus;
  net_days: number;
  created_at: string;
  tag_count: number;
}

export interface PartnerRecord {
  id: string;
  code: string;
  legal_name: string;
  display_name: string;
  contact_name: string | null;
  contact_email: string | null;
  statement_email: string | null;
  status: PartnerStatus;
  tax_number: string | null;
  tax_applies: boolean;
  tax_rate_pct: number | string;
  payment_method_text: string | null;
  net_days: number;
  notes: string | null;
  landing_content: { headline?: string; body?: string; logo_url?: string } | null;
  created_at?: string;
  /** The Coach Cedar house partner (migration 1876): its own codes, never a bounty, API key or statement. */
  is_house?: boolean;
}

export interface TagRow {
  id: string;
  partner_id: string;
  tag: string;
  name: string;
  bounty_cents: number;
  currency: string;
  clipboard_handoff: boolean;
  status: PartnerStatus;
  created_at: string;
  trial_days: number;
  discount_monthly_pct: number | null;
  /** How long the monthly discount lasts (migration 1845): 1, 3, 6 or 12; null with none. */
  discount_monthly_months?: number | null;
  discount_annual_pct: number | null;
  bounty_monthly_cents: number;
  bounty_annual_cents: number;
  offer_kind: 'tracking_only' | 'trial' | 'discount' | 'trial_and_discount';
  offer_summary: string;
  /** Partner webhook intake (S1, migration 2090): review first (default) or send automatically. */
  contact_send_mode?: 'review' | 'automatic';
  contact_send_mode_set_at?: string | null;
}

export interface LinkRow {
  id: string;
  tag_id: string;
  tag?: string;
  destination: string;
  url: string;
  created_at: string;
}

export interface CodeRow {
  id: string;
  code: string;
  kind: CodeKind;
  external_ref: string | null;
  max_redemptions: number | null;
  redemptions: number;
  status: string;
  state: CodeState;
  expires_at: string | null;
  created_via: string;
  created_at: string;
  revoked_at: string | null;
  redeemed_at: string | null;
  tag_id: string;
  tag: string;
  partner_id: string;
  partner_code: string;
}

export interface DiscountLevel {
  pct: number;
  status: 'active' | 'retired';
  tags_using?: number;
}

export interface StatementRow {
  id: string;
  number: string;
  partner_id: string;
  partner_code: string;
  display_name: string;
  period_start: string;
  period_end: string;
  status: StatementStatus;
  subtotal_cents: number;
  tax_cents: number;
  total_cents: number;
  currency: string;
  statement_date: string;
  due_date: string | null;
  approved_at: string | null;
  sent_at: string | null;
  paid_at: string | null;
  paid_reference: string | null;
  supersedes_id: string | null;
}

export interface StatementLine {
  id: string;
  ledger_id: string;
  join_date: string;
  tag: string;
  platform: string | null;
  amount_cents: number;
  kind: string;
  prior_period?: boolean;
  prior_period_month?: string | null;
  /** The plan the line's bounty was earned on (partner_ledger.billing_period, migration 1836). */
  billing_period?: string | null;
}

export interface StatementDetail extends StatementRow {
  legal_name: string;
  statement_email: string | null;
  contact_email: string | null;
  tax_number: string | null;
  net_days: number;
  payment_method_text: string | null;
  note?: string | null;
  last_resent_at?: string | null;
  pdf_key?: string | null;
  csv_key?: string | null;
  lines: StatementLine[];
}

export interface ReviewFlag {
  code: string;
  message: string;
  detail?: Record<string, unknown>;
}

export interface HealthAlert {
  code: string;
  partner_id: string;
  partner_code: string;
  partner_name: string;
  tag_id: string | null;
  tag: string | null;
  message: string;
  numbers: Record<string, number | null>;
}

export const FUNNEL_STAGES = [
  'clicks', 'store_taps', 'installs', 'accounts', 'first_plans', 'first_charges', 'qualified',
] as const;
export type FunnelStage = (typeof FUNNEL_STAGES)[number];

export const FUNNEL_STAGE_LABELS: Record<FunnelStage, string> = {
  clicks: 'Clicks',
  store_taps: 'Store taps',
  installs: 'Installs',
  accounts: 'Accounts',
  first_plans: 'First plans',
  first_charges: 'First charges',
  qualified: 'Qualified',
};

/**
 * S1 (#5418): accounts the tag brought in with no click on record — an offer code typed in the app,
 * the email match, or a click older than the retention. They are INCLUDED in `accounts`, so the
 * screen shows them as a breakdown of accounts, never as a stage of their own.
 */
type AccountsWithoutClick = { accounts_without_click?: number };

export interface FunnelRow extends Record<FunnelStage, number>, AccountsWithoutClick {
  tag: string;
  platform: string;
  accrued_cents: number;
}

export interface Funnel {
  window_days: number;
  rows: FunnelRow[];
  totals: Record<FunnelStage, number> & { accrued_cents: number } & AccountsWithoutClick;
  installs_by_method: Record<string, number>;
  ambiguous_checkins_platform_wide: number;
  apple_aggregate: number | null;
  notes: Record<string, string>;
}

export interface LedgerEntry {
  id: string;
  tag_id: string | null;
  tag: string | null;
  entry_type: 'accrual' | 'reversal' | 'payment' | 'adjustment';
  amount_cents: number;
  currency: string;
  occurred_at: string;
  created_at: string;
  note: string | null;
  has_user: boolean;
  statement_id: string | null;
  statement_number: string | null;
  statement_status: StatementStatus | null;
  /** monthly | annual | unknown; null for a payment, an adjustment or a pre-1836 entry. */
  billing_period?: string | null;
}

/** The Plan column: the period a bounty was earned on (migration 1836). "Unknown" is an
 * accrual whose charge period could not be read (paid the lower bounty; the statement is
 * flagged for review); "—" is an entry with no period (payment, adjustment, pre-1836). */
export function planLabel(billingPeriod: string | null | undefined): string {
  switch ((billingPeriod ?? '').trim().toLowerCase()) {
    case 'monthly': return 'Monthly';
    case 'annual': return 'Annual';
    case 'unknown': return 'Unknown';
    default: return '—';
  }
}

export interface ProgramStatus {
  attribution_enabled: boolean;
  ended_terminal: boolean;
  pause_judged_at_tap: boolean;
  payer_copy_enabled: boolean;
  payer_identity_gaps: string[];
  trial_days_allowed: number[];
}

export interface EmailPreview {
  number: string;
  status: StatementStatus;
  to_email: string | null;
  to_source: 'statement_email' | 'contact_email' | null;
  subject: string;
  reply_to: string | null;
  attachments: string[];
  html: string | null;
  text: string | null;
  sendable: boolean;
  reason: string | null;
}

// ── roles ─────────────────────────────────────────────────────────────────────

/** PARTNER_WRITE_ROLES / STATEMENT_WRITE_ROLES on the backend. Support and viewer read only. */
export const WRITE_ROLES = ['super_admin', 'admin'] as const;

/** Fails closed: an unknown or missing role reads only (the server would answer 403 anyway). */
export function canWrite(role: string | null | undefined): boolean {
  return !!role && (WRITE_ROLES as readonly string[]).includes(role);
}

// ── money ─────────────────────────────────────────────────────────────────────

/** services.partner_statements.cents_to_text, byte for byte: "CAD 1,234.50", "-CAD 5.00". */
export function formatCents(cents: number | null | undefined, currency = 'CAD'): string {
  const n = Number(cents ?? 0);
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(Math.trunc(n)) / 100;
  return `${sign}${currency} ${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** The backend's ceiling for a bounty and the bound of an adjustment: CAD 5,000.00. */
export const MAX_CENTS = 500000;

export type CentsParse = { ok: true; cents: number } | { ok: false; error: string };

/**
 * A dollar amount typed by an admin ("25", "25.5", "1,250.00", "-10") as whole cents.
 * `signed` allows a leading minus (a ledger adjustment); otherwise it must be ≥ 0.
 */
export function parseDollarsToCents(text: string, opts: { signed?: boolean; nonZero?: boolean } = {}): CentsParse {
  const raw = (text ?? '').trim().replace(/,/g, '').replace(/^\$/, '');
  if (!raw) return { ok: false, error: 'Enter an amount.' };
  const m = /^(-)?\$?(\d+)(?:\.(\d{1,2}))?$/.exec(raw);
  if (!m) return { ok: false, error: 'Use a number with at most two decimals (e.g. 25 or 25.50).' };
  if (m[1] && !opts.signed) return { ok: false, error: 'The amount cannot be negative.' };
  const cents = Number(m[2]) * 100 + Number((m[3] ?? '').padEnd(2, '0') || '0');
  const value = m[1] ? -cents : cents;
  if (opts.nonZero && value === 0) return { ok: false, error: 'The amount cannot be zero.' };
  if (Math.abs(value) > MAX_CENTS) return { ok: false, error: `At most ${formatCents(MAX_CENTS)}.` };
  return { ok: true, cents: value };
}

export function centsToInput(cents: number | null | undefined): string {
  const n = Number(cents ?? 0);
  return (n / 100).toFixed(2);
}

// ── errors ────────────────────────────────────────────────────────────────────

/** The `detail` of a FastAPI error body (its text, as adminFetch hands it over), or null. */
export function errorDetail(bodyText: string): unknown {
  if (!bodyText) return null;
  try {
    const parsed = JSON.parse(bodyText);
    return parsed && typeof parsed === 'object' && 'detail' in parsed ? parsed.detail : null;
  } catch {
    return null;
  }
}

/** The server's own words for a refusal: a string detail, `{message}`, or a 422 list. */
/**
 * A server `detail` as one readable line, or null when it carries none: a string as is,
 * FastAPI's 422 list flattened ("count: Input should be a valid integer; wave: Field
 * required"), a `{message}` its message. Never an array or an object handed on as a message.
 */
export function detailText(detail: unknown): string | null {
  const d = detail as
    | string
    | { message?: unknown }
    | Array<{ loc?: unknown[]; msg?: unknown } | null>
    | null
    | undefined;
  if (typeof d === 'string') return d || null;
  if (Array.isArray(d)) {
    const parts = d.map((e) => {
      const msg = e && typeof e.msg === 'string' && e.msg ? e.msg : 'invalid';
      const field = e && Array.isArray(e.loc) ? e.loc.filter((x) => x !== 'body').join('.') : '';
      return field ? `${field}: ${msg}` : msg;
    });
    return parts.length ? parts.join('; ') : null;
  }
  if (d && typeof d === 'object' && typeof d.message === 'string' && d.message) return d.message;
  return null;
}

export function describeApiError(status: number, bodyText: string): string {
  const text = detailText(errorDetail(bodyText));
  if (text) return text;
  if (status === 403) return 'Your admin role cannot do this (admin or super admin only).';
  if (status === 401) return 'Your admin session has expired. Sign in again.';
  if (status === 503) return 'The service is not ready (a deploy may be finishing) — try again in a few minutes.';
  return `Request failed (HTTP ${status}).`;
}

/** The structured `{code, …}` detail of a 409 (e.g. user_has_no_bounty_entry, files_not_stored). */
export function errorCode(bodyText: string): string | null {
  const d = errorDetail(bodyText) as { code?: unknown } | null;
  return d && typeof d === 'object' && typeof d.code === 'string' ? d.code : null;
}

/**
 * Approve & send / resend refuse (400) when Coach Cedar's payer details are not
 * configured (services.partner_statements.require_payer_identity). That is a backend
 * setting, not something the admin can fix on this screen — so it is called out as such.
 */
export function isPayerIdentityRefusal(message: string | null | undefined): boolean {
  return !!message && /payer details/i.test(message) && /not configured/i.test(message);
}

// ── partner status (pause / end; ended is final) ─────────────────────────────

/**
 * The status moves an admin may offer. ENDED is terminal while FF_PARTNER_ENDED_TERMINAL
 * is on (the server answers 409); with it off an ended partner can be reactivated.
 */
export function statusMoves(status: PartnerStatus, endedTerminal: boolean): PartnerStatus[] {
  if (status === 'active') return ['paused', 'ended'];
  if (status === 'paused') return ['active', 'ended'];
  return endedTerminal ? [] : ['active'];
}

/**
 * What a pause / resume / end does, in the words of the rule that is LIVE:
 * `FF_PARTNER_PAUSE_JUDGED_AT_TAP` (program-status `pause_judged_at_tap`). ON (the code
 * default): a pause is judged at the TAP and an end by its time — a click tapped while
 * active can still earn later, one tapped while paused never does. OFF (the rule before
 * 2026-09-26): the pause and the end are judged when the iPhone match / the signup bind
 * RUN, so a lead that finishes while paused or after the end earns nothing, whenever it
 * tapped. Unknown (the switches could not be read) says so rather than guessing.
 */
export function statusMoveCopy(
  target: PartnerStatus,
  subject: 'partner' | 'tag',
  judgedAtTap: boolean | null,
): string {
  const its = subject === 'partner' ? 'Its links' : 'The link';
  const unknown = judgedAtTap === null ? ' (Could not read which rule is live — check the program switches.)' : '';
  if (target === 'paused') {
    const rule = judgedAtTap === false
      ? 'nothing attributes while paused — a lead is judged when the person installs and signs up, so a click tapped before the pause that finishes during it does not earn either'
      : 'a click tapped while paused never earns — even after you resume; one tapped before the pause can still earn';
    return `${its} keep logging clicks, but ${rule}. Users already attributed keep qualifying.` +
      (subject === 'partner' ? ' Its codes still grant their offer.' : '') + unknown;
  }
  if (target === 'active') {
    const rule = judgedAtTap === false
      ? 'including clicks tapped during the pause whose signup finishes after you resume (the pause is judged at the signup while the tap-time rule is off)'
      : 'a click tapped while paused still never earns (it is judged at the tap)';
    return `${its} attribute again from now on — ${rule}.${unknown}`;
  }
  const endRule = judgedAtTap === false
    ? 'once ended, nothing attributes any more — even a click tapped before the end, if its signup finishes after it'
    : 'a click tapped before the end can still earn if the signup finishes later; nothing tapped after it ever does';
  const tail = subject === 'partner'
    ? ' The next monthly draft makes a final statement.'
    : ' Entries it already earned are still billed.';
  return `Ending is final: it cannot be undone. ${its} stop logging; ${endRule}.${tail}${unknown}`;
}

export function statusBadge(status: string): 'success' | 'warning' | 'neutral' | 'error' | 'info' {
  switch (status) {
    case 'active':
    case 'paid':
      return 'success';
    case 'paused':
    case 'draft':
      return 'warning';
    case 'approved':
    case 'sent':
      return 'info';
    case 'void':
    case 'revoked':
      return 'error';
    default:
      return 'neutral';
  }
}

/** Typed confirmation for destructive actions (design §5.3). Case-insensitive, trimmed. */
export function typedConfirmationMatches(input: string, expected: string): boolean {
  return !!expected && input.trim().toUpperCase() === expected.trim().toUpperCase();
}

// ── the home strip (owed / overdue / drafts) ─────────────────────────────────

export interface HomeStrip {
  owedCents: number;
  owedCount: number;
  overdueCents: number;
  overdueCount: number;
  draftCount: number;
}

/** "YYYY-MM-DD" of a Date in local time (a due date is a calendar day). */
export function isoDay(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Owed = approved or sent (not paid, not void); overdue = owed with a due date before today;
 * drafts awaiting review. Handoff §2.1.
 */
export function homeStrip(statements: StatementRow[], today: string): HomeStrip {
  const out: HomeStrip = { owedCents: 0, owedCount: 0, overdueCents: 0, overdueCount: 0, draftCount: 0 };
  for (const s of statements) {
    if (s.status === 'draft') out.draftCount += 1;
    if (s.status === 'approved' || s.status === 'sent') {
      out.owedCents += Number(s.total_cents || 0);
      out.owedCount += 1;
      if (s.due_date && s.due_date.slice(0, 10) < today) {
        out.overdueCents += Number(s.total_cents || 0);
        out.overdueCount += 1;
      }
    }
  }
  return out;
}

/** The latest non-void statement per partner (list_statements is newest first). */
export function latestStatementByPartner(statements: StatementRow[]): Record<string, StatementRow> {
  const out: Record<string, StatementRow> = {};
  for (const s of statements) {
    if (s.status === 'void') continue;
    if (!out[s.partner_id]) out[s.partner_id] = s;
  }
  return out;
}

export function isOverdue(s: Pick<StatementRow, 'status' | 'due_date'>, today: string): boolean {
  return (s.status === 'approved' || s.status === 'sent') && !!s.due_date && s.due_date.slice(0, 10) < today;
}

// ── statement actions by status ──────────────────────────────────────────────

export interface StatementActions {
  approveSend: boolean;
  note: boolean;
  markPaid: boolean;
  voidReissue: boolean;
  resend: boolean;
}

/** draft → approved → sent → paid, or void (never from paid). */
export function statementActions(status: StatementStatus): StatementActions {
  return {
    approveSend: status === 'draft' || status === 'approved',
    note: status === 'draft' || status === 'approved',
    markPaid: status === 'approved' || status === 'sent',
    voidReissue: status === 'draft' || status === 'approved' || status === 'sent',
    resend: status === 'sent' || status === 'paid',
  };
}

export const APPROVE_REASON_COPY: Record<string, string> = {
  not_requested: 'Approved without an email (you chose not to send it).',
  no_statement_email: 'Approved, but NOT emailed: the partner has no statement or contact email. Add one in Settings, then use Approve & send again.',
  sendgrid_not_configured: 'Approved, but NOT emailed: the email service is not configured on the server.',
  email_error: 'Approved, but NOT emailed: the email service failed. Try Approve & send again.',
};

/** The line to show after approve & send answers. */
export function approveResultMessage(res: { status: string; emailed: boolean; reason: string }): string {
  if (res.emailed) return 'Sent. The partner has the PDF and CSV; the status is now sent.';
  return APPROVE_REASON_COPY[res.reason] ?? `Approved, but NOT emailed (${res.reason}).`;
}

export function resendResultMessage(res: { emailed: boolean; reason: string; to_email: string | null; files_stored?: boolean }): string {
  const stored = res.files_stored ? ' The files were stored now, so the download links work.' : '';
  if (res.emailed) return `Re-sent to ${res.to_email}. The status did not change.${stored}`;
  return `NOT re-sent (${res.reason}). Nothing else changed.${stored}`;
}

/** The review flags are advice; `null` means they could not be computed — never "nothing unusual". */
export function flagsSummary(flags: ReviewFlag[] | null, flagsError: string | null): string {
  if (flags === null || flagsError) return 'Flags unavailable — review by hand.';
  if (flags.length === 0) return 'Nothing unusual flagged.';
  return `${flags.length} flag${flags.length === 1 ? '' : 's'} to review.`;
}

// ── offers (owner rulings 16–18b) ────────────────────────────────────────────

export const TRIAL_CHOICES = [7, 14, 30] as const;

/**
 * What a NEW partner tag starts with (owner decision, partner launch 2026-10-17, plan §3 O1 + O3):
 * a free month, and CAD 10 per monthly subscriber / CAD 30 per annual one. The admin can change
 * either before creating the tag; a Coach Cedar (house) tag keeps the standard 7 days and pays none.
 */
export const DEFAULT_PARTNER_TRIAL_DAYS: number = 30;
export const DEFAULT_BOUNTY_MONTHLY_INPUT: string = '10.00';
export const DEFAULT_BOUNTY_ANNUAL_INPUT: string = '30.00';

export function trialLabel(days: number): string {
  if (days === 14) return '2 weeks (14 days)';
  if (days === 30) return '1 month (30 days)';
  if (days === 7) return '7 days (default)';
  return `${days} days`;
}

export interface OfferForm {
  trial_days: number;
  discount_monthly_pct: number | null;
  /** The monthly discount's length in months (ignored without a monthly discount). */
  discount_monthly_months: number;
  discount_annual_pct: number | null;
  bounty_monthly: string;
  bounty_annual: string;
}

export function offerFormFromTag(tag: TagRow): OfferForm {
  return {
    trial_days: tag.trial_days ?? 7,
    discount_monthly_pct: tag.discount_monthly_pct ?? null,
    discount_monthly_months: tag.discount_monthly_months ?? DEFAULT_MONTHLY_TERM,
    discount_annual_pct: tag.discount_annual_pct ?? null,
    bounty_monthly: centsToInput(tag.bounty_monthly_cents),
    bounty_annual: centsToInput(tag.bounty_annual_cents),
  };
}

/**
 * The discount levels a picker may offer for one period: every ACTIVE level, plus the
 * level the tag already carries even if it has been retired since (the server allows
 * keeping it, never picking a retired one anew).
 */
export function discountChoices(levels: DiscountLevel[], current: number | null): number[] {
  const out = levels.filter((l) => l.status === 'active').map((l) => l.pct);
  if (current != null && !out.includes(current)) out.push(current);
  return out.sort((a, b) => a - b);
}

export type OfferPatch = Partial<{
  trial_days: number;
  discount_monthly_pct: number | null;
  discount_monthly_months: number;
  discount_annual_pct: number | null;
  bounty_monthly_cents: number;
  bounty_annual_cents: number;
}>;

/**
 * The PATCH /tags/{id}/offer body: only what moved. Errors keyed by field. The trial is
 * 7/14/30 only; a discount is an active level (or the one the tag already has) or none.
 */
export function offerPatch(
  tag: TagRow,
  form: OfferForm,
  levels: DiscountLevel[],
  offers?: DiscountOffer[],
): { body: OfferPatch; errors: Record<string, string> } {
  const errors: Record<string, string> = {};
  const body: OfferPatch = {};
  if (!(TRIAL_CHOICES as readonly number[]).includes(form.trial_days)) {
    errors.trial_days = 'The free trial is 7 days, 2 weeks or 1 month — nothing else.';
  } else if (form.trial_days !== tag.trial_days) {
    body.trial_days = form.trial_days;
  }
  for (const [key, current] of [
    ['discount_monthly_pct', tag.discount_monthly_pct],
    ['discount_annual_pct', tag.discount_annual_pct],
  ] as const) {
    const next = form[key];
    if (next != null && !discountChoices(levels, current ?? null).includes(next)) {
      errors[key] = `${next}% is not an active discount level.`;
    } else if ((next ?? null) !== (current ?? null)) {
      body[key] = next ?? null;
    }
  }
  // The monthly discount's length (migration 1845): sent whenever a monthly discount is set and
  // its length moved; with no monthly discount there is no length to send (the server clears it).
  if (form.discount_monthly_pct != null && !errors.discount_monthly_pct) {
    const currentMonths = tag.discount_monthly_pct != null ? (tag.discount_monthly_months ?? DEFAULT_MONTHLY_TERM) : null;
    const choices = monthlyTermChoices(offers, form.discount_monthly_pct,
      { pct: tag.discount_monthly_pct ?? null, months: tag.discount_monthly_months ?? null });
    if (!choices.includes(form.discount_monthly_months)) {
      errors.discount_monthly_months = `${form.discount_monthly_pct}% off for ${form.discount_monthly_months} months is not on the menu.`;
    } else if (form.discount_monthly_months !== currentMonths || body.discount_monthly_pct !== undefined) {
      body.discount_monthly_months = form.discount_monthly_months;
    }
  }
  for (const [field, key, current] of [
    ['bounty_monthly', 'bounty_monthly_cents', tag.bounty_monthly_cents],
    ['bounty_annual', 'bounty_annual_cents', tag.bounty_annual_cents],
  ] as const) {
    const parsed = parseDollarsToCents(form[field]);
    if (!parsed.ok) errors[field] = parsed.error;
    else if (parsed.cents !== Number(current ?? 0)) body[key] = parsed.cents;
  }
  return { body, errors };
}

/** Plain words for a tag's offer, from the typed fields (the server's `offer_summary` wins when present). */
export function offerKindLabel(kind: TagRow['offer_kind']): string {
  switch (kind) {
    case 'tracking_only':
      return 'Tracking only';
    case 'trial':
      return 'Longer trial';
    case 'discount':
      return 'Discount';
    case 'trial_and_discount':
      return 'Longer trial + discount';
    default:
      return kind;
  }
}

export function bountyLabel(tag: Pick<TagRow, 'bounty_monthly_cents' | 'bounty_annual_cents' | 'currency'>): string {
  const m = formatCents(tag.bounty_monthly_cents, tag.currency || 'CAD');
  if (tag.bounty_monthly_cents === tag.bounty_annual_cents) return `${m} (every plan)`;
  return `${m} monthly · ${formatCents(tag.bounty_annual_cents, tag.currency || 'CAD')} annual`;
}

// ── tags and links ───────────────────────────────────────────────────────────

export const TAG_RE = /^[a-z0-9][a-z0-9-]{1,39}$/;
export const PARTNER_CODE_RE = /^[A-Z0-9][A-Z0-9-]{1,15}$/;
export const LINK_DESTINATIONS = ['home', 'about', 'blog', 'support', 'with'] as const;

/** The /go/<tag> link a partner prints (services.partner_admin.build_link_url, home destination). */
export function goLink(tag: string): string {
  return `${SITE_BASE}/go/${tag}`;
}

/**
 * The tag's **Partner page** link — the co-branded page (partner launch UC2):
 * `https://coachcedar.com/go/<tag>?d=with&p=<partner code, lower-cased>`, exactly what
 * services.partner_admin.build_link_url emits for the `with` destination. The /go page records
 * the click and then shows the partner's page; the bare coachcedar.com/with/<code> address on its
 * own records no click, so nothing it brings in is attributed.
 */
export function partnerPageLink(tag: string, partnerCode: string): string {
  return `${SITE_BASE}/go/${tag}?d=with&p=${partnerCode.toLowerCase()}`;
}

// ── the co-branded page (landing_content) ────────────────────────────────────

/** services.partner_admin.LANDING_CONTENT_FIELDS: the server refuses anything longer. */
export const LANDING_HEADLINE_MAX = 200;
export const LANDING_BODY_MAX = 2000;
/** The logo upload (plan E10: self-hosted on our storage) — the upload route's own rule (S1). */
export const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'] as const;
export const LOGO_MAX_BYTES = 512 * 1024;

/**
 * The PATCH body for the co-branded page alone: `{landing_content}` with only what is set
 * (trimmed) — or null when nothing is, which clears the page. The logo URL is never typed here:
 * it is whatever the upload stored (or empty to remove it).
 */
export function landingContentBody(f: { headline: string; body: string; logoUrl: string }): {
  body: { landing_content: PartnerRecord['landing_content'] };
  errors: string[];
} {
  const errors: string[] = [];
  const headline = (f.headline ?? '').trim();
  const text = (f.body ?? '').trim();
  const logo = (f.logoUrl ?? '').trim();
  if (headline.length > LANDING_HEADLINE_MAX) errors.push(`Headline: at most ${LANDING_HEADLINE_MAX} characters.`);
  if (text.length > LANDING_BODY_MAX) errors.push(`Message: at most ${LANDING_BODY_MAX.toLocaleString('en-CA')} characters.`);
  const lc: { headline?: string; body?: string; logo_url?: string } = {};
  if (headline) lc.headline = headline;
  if (text) lc.body = text;
  if (logo) lc.logo_url = logo;
  return { body: { landing_content: Object.keys(lc).length ? lc : null }, errors };
}

/** A logo file the upload will accept, or the reason it won't. */
export function checkLogoFile(f: { name: string; size: number; type: string } | null | undefined): string | null {
  if (!f) return 'Choose a logo file first.';
  if (!(LOGO_TYPES as readonly string[]).includes(f.type)) return 'The logo must be a PNG, JPEG, WebP or SVG image.';
  if (!f.size) return 'That file is empty.';
  if (f.size > LOGO_MAX_BYTES) return 'The logo must be 512 KB or smaller.';
  return null;
}

export const CLIPBOARD_HANDOFF_NOTE =
  'Off by default (design §7 decision 1). When ANY active tag has it on, the app asks for paste ' +
  'permission platform-wide to read the tag a visitor copied on the website. Turn on only when the partner needs it.';

export const BOUNTY_FORWARD_NOTE =
  'A bounty change applies FORWARD only: entries already earned keep the bounty they were earned at.';

// ── the partner record (new + settings) ──────────────────────────────────────

/** The tag moves an admin may offer: none while the partner has ended (its tags follow it). */
export function tagStatusMoves(tagStatus: PartnerStatus, partnerStatus: PartnerStatus, endedTerminal: boolean): PartnerStatus[] {
  if (partnerStatus === 'ended') return [];
  return statusMoves(tagStatus, endedTerminal);
}

/** Whether a tag's offer, links and codes can still be changed (an ended tag or partner: no). */
export function tagEditable(tagStatus: PartnerStatus, partnerStatus: PartnerStatus): boolean {
  return tagStatus !== 'ended' && partnerStatus !== 'ended';
}

/** The PDF reads these fields as markup: the server refuses a `<` or `&#` in them. */
export const PDF_MARKUP_FIELDS = ['legal_name', 'display_name', 'tax_number', 'payment_method_text'] as const;

export function hasPdfMarkup(text: string | null | undefined): boolean {
  return !!text && (text.includes('<') || text.includes('&#'));
}

export interface PartnerForm {
  code: string;
  legal_name: string;
  display_name: string;
  contact_name: string;
  contact_email: string;
  statement_email: string;
  tax_number: string;
  tax_applies: boolean;
  tax_rate_pct: string;
  payment_method_text: string;
  net_days: string;
  notes: string;
  landing_headline: string;
  landing_body: string;
  landing_logo_url: string;
}

export function emptyPartnerForm(): PartnerForm {
  return {
    code: '', legal_name: '', display_name: '', contact_name: '', contact_email: '', statement_email: '',
    tax_number: '', tax_applies: false, tax_rate_pct: '0', payment_method_text: '', net_days: '30', notes: '',
    landing_headline: '', landing_body: '', landing_logo_url: '',
  };
}

export function partnerFormFromRecord(p: PartnerRecord): PartnerForm {
  const lc = p.landing_content || {};
  return {
    code: p.code,
    legal_name: p.legal_name ?? '',
    display_name: p.display_name ?? '',
    contact_name: p.contact_name ?? '',
    contact_email: p.contact_email ?? '',
    statement_email: p.statement_email ?? '',
    tax_number: p.tax_number ?? '',
    tax_applies: !!p.tax_applies,
    tax_rate_pct: String(p.tax_rate_pct ?? 0),
    payment_method_text: p.payment_method_text ?? '',
    net_days: String(p.net_days ?? 30),
    notes: p.notes ?? '',
    landing_headline: lc.headline ?? '',
    landing_body: lc.body ?? '',
    landing_logo_url: lc.logo_url ?? '',
  };
}

type PartnerFields = Record<string, unknown>;

function nullable(text: string): string | null {
  const t = text.trim();
  return t ? t : null;
}

function landingFrom(form: PartnerForm): PartnerRecord['landing_content'] {
  const lc: { headline?: string; body?: string; logo_url?: string } = {};
  if (form.landing_headline.trim()) lc.headline = form.landing_headline.trim();
  if (form.landing_body.trim()) lc.body = form.landing_body.trim();
  if (form.landing_logo_url.trim()) lc.logo_url = form.landing_logo_url.trim();
  return Object.keys(lc).length ? lc : null;
}

/** Validate the fields both create and edit share; returns the typed field map. */
function partnerFields(form: PartnerForm, errors: Record<string, string>): PartnerFields {
  const out: PartnerFields = {};
  for (const f of ['legal_name', 'display_name'] as const) {
    if (!form[f].trim()) errors[f] = 'Required.';
  }
  for (const f of PDF_MARKUP_FIELDS) {
    if (hasPdfMarkup(form[f])) errors[f] = 'Cannot contain "<" or "&#" (the statement PDF reads them as markup).';
  }
  const netDays = Number(form.net_days);
  if (!/^\d+$/.test(form.net_days.trim()) || netDays > 3650) errors.net_days = 'Whole days (0 or more).';
  const rate = Number(form.tax_rate_pct);
  if (form.tax_rate_pct.trim() === '' || !Number.isFinite(rate) || rate < 0 || rate > 100) {
    errors.tax_rate_pct = 'A percentage from 0 to 100.';
  }
  const logo = form.landing_logo_url.trim();
  if (logo && !/^https:\/\//i.test(logo)) errors.landing_logo_url = 'Use an https:// address.';
  out.legal_name = form.legal_name.trim();
  out.display_name = form.display_name.trim();
  out.contact_name = nullable(form.contact_name);
  out.contact_email = nullable(form.contact_email);
  out.statement_email = nullable(form.statement_email);
  out.tax_number = nullable(form.tax_number);
  out.tax_applies = form.tax_applies;
  out.tax_rate_pct = rate;
  out.payment_method_text = nullable(form.payment_method_text);
  out.net_days = netDays;
  out.notes = nullable(form.notes);
  out.landing_content = landingFrom(form);
  return out;
}

export function createPartnerBody(form: PartnerForm): { body: PartnerFields; errors: Record<string, string> } {
  const errors: Record<string, string> = {};
  const code = form.code.trim().toUpperCase();
  if (!PARTNER_CODE_RE.test(code)) {
    errors.code = '2–16 characters: uppercase letters, digits or "-" (it appears in statement numbers and never changes).';
  }
  const body = { code, ...partnerFields(form, errors) };
  return { body, errors };
}

/** PATCH /partners/{id}: only fields that changed; `code` and `status` are never sent. */
export function partnerPatchBody(
  original: PartnerRecord,
  form: PartnerForm,
): { body: PartnerFields; errors: Record<string, string> } {
  const errors: Record<string, string> = {};
  const next = partnerFields(form, errors);
  const before = partnerFields(partnerFormFromRecord(original), {});
  const body: PartnerFields = {};
  for (const [k, v] of Object.entries(next)) {
    if (JSON.stringify(v) !== JSON.stringify(before[k])) body[k] = v;
  }
  return { body, errors };
}

// ── codes ────────────────────────────────────────────────────────────────────

export const CODE_STATES: CodeState[] = ['active', 'expired', 'used_up', 'redeemed', 'revoked', 'replaced'];

export const CODE_STATE_LABELS: Record<CodeState, string> = {
  active: 'Active',
  expired: 'Expired',
  used_up: 'Used up',
  redeemed: 'Redeemed',
  revoked: 'Revoked',
  replaced: 'Replaced',
};

export function codeStateBadge(state: CodeState): 'success' | 'warning' | 'neutral' | 'error' | 'info' {
  if (state === 'active') return 'success';
  if (state === 'redeemed' || state === 'used_up') return 'info';
  if (state === 'revoked') return 'error';
  if (state === 'expired') return 'warning';
  return 'neutral';
}

/** A code as the partner prints it → its stored match form (spaces and dashes ignored, A-Z/0-9, 4–32). */
export function normalizeCode(text: string): string {
  return (text ?? '').replace(/[\s-]/g, '').toUpperCase();
}

export const CODE_RE = /^[A-Z0-9]{4,32}$/;

export interface CodeFilters {
  partner_id?: string;
  tag_id?: string;
  kind?: CodeKind | '';
  state?: CodeState | '';
  external_ref?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

/** The query string for GET /codes and /codes/export (empty values dropped, `q` normalized). */
export function codesQuery(f: CodeFilters): string {
  const p = new URLSearchParams();
  const put = (k: string, v: unknown) => {
    if (v === undefined || v === null || v === '') return;
    p.set(k, String(v));
  };
  put('partner_id', f.partner_id);
  put('tag_id', f.tag_id);
  put('kind', f.kind);
  put('state', f.state);
  put('external_ref', f.external_ref?.trim());
  put('q', f.q ? normalizeCode(f.q) : '');
  put('limit', f.limit);
  put('offset', f.offset);
  const s = p.toString();
  return s ? `?${s}` : '';
}

/** One reference per line; blank lines ignored. Errors for repeats, over-long refs, too many. */
export function parseExternalRefs(text: string): { refs: string[]; errors: string[] } {
  const refs = (text ?? '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const errors: string[] = [];
  const seen = new Set<string>();
  const repeats = new Set<string>();
  for (const r of refs) {
    if (seen.has(r)) repeats.add(r);
    seen.add(r);
    if (r.length > 200) errors.push(`A reference is longer than 200 characters: "${r.slice(0, 30)}…"`);
  }
  if (repeats.size) errors.push(`Each reference once — repeated: ${Array.from(repeats).slice(0, 5).join(', ')}`);
  if (refs.length > 1000) errors.push('At most 1,000 references per batch.');
  return { refs, errors };
}

export interface SingleUseForm {
  mode: 'count' | 'refs';
  count: string;
  refsText: string;
  prefix: string;
  expiresInDays: string;
}

export function singleUseBody(form: SingleUseForm): { body: Record<string, unknown>; errors: string[] } {
  const errors: string[] = [];
  const body: Record<string, unknown> = {};
  if (form.mode === 'count') {
    const n = Number(form.count);
    if (!/^\d+$/.test(form.count.trim()) || n < 1 || n > 1000) errors.push('Count: 1 to 1,000 codes.');
    else body.count = n;
  } else {
    const { refs, errors: refErrors } = parseExternalRefs(form.refsText);
    errors.push(...refErrors);
    if (!refs.length) errors.push('Paste at least one reference (one per line).');
    body.external_refs = refs;
  }
  const prefix = normalizeCode(form.prefix);
  if (prefix) {
    if (!/^[A-Z0-9]{1,22}$/.test(prefix)) errors.push('Prefix: letters and digits, at most 22.');
    else body.prefix = prefix;
  }
  const days = Number(form.expiresInDays);
  if (!/^\d+$/.test(form.expiresInDays.trim()) || days < 1 || days > 730) errors.push('Expires in: 1 to 730 days.');
  else body.expires_in_days = days;
  return { body, errors };
}

/**
 * An expiry picked as a calendar day → the end of that day in the admin's time zone, as
 * an ISO time WITH a zone (the server refuses a zone-less or past time).
 */
export function endOfDayIso(day: string, now: Date = new Date()): { iso: string | null; error: string | null } {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return { iso: null, error: 'Pick a date.' };
  const [y, m, d] = day.split('-').map(Number);
  const at = new Date(y, m - 1, d, 23, 59, 59);
  if (Number.isNaN(at.getTime())) return { iso: null, error: 'Pick a valid date.' };
  if (at.getTime() <= now.getTime()) return { iso: null, error: 'The date must be in the future.' };
  return { iso: at.toISOString(), error: null };
}

export function sharedCodeBody(form: { code: string; maxRedemptions: string; expiresOn: string }, now?: Date): {
  body: Record<string, unknown>;
  errors: string[];
} {
  const errors: string[] = [];
  const code = normalizeCode(form.code);
  if (!CODE_RE.test(code)) errors.push('The code: 4 to 32 letters and digits (spaces and dashes are ignored).');
  const body: Record<string, unknown> = { code };
  if (form.maxRedemptions.trim()) {
    const n = Number(form.maxRedemptions);
    if (!/^\d+$/.test(form.maxRedemptions.trim()) || n < 1) errors.push('Limit: a whole number of 1 or more (blank = unlimited).');
    else body.max_redemptions = n;
  }
  if (form.expiresOn.trim()) {
    const { iso, error } = endOfDayIso(form.expiresOn.trim(), now);
    if (error) errors.push(`Expires: ${error}`);
    else body.expires_at = iso;
  }
  return { body, errors };
}

/** Whether a code row can still be changed (revoked/replaced codes cannot — create a new one). */
export function codeEditable(row: Pick<CodeRow, 'state'>): boolean {
  return row.state !== 'revoked' && row.state !== 'replaced';
}

// ── funnel ───────────────────────────────────────────────────────────────────

/**
 * Stage-to-stage conversion, as a percentage with one decimal. Installs → accounts counts only the
 * accounts that came through a click (accounts without a click never installed from one); accounts →
 * first plans counts every account. Null when the earlier stage is 0, or when the later one
 * outnumbers it (the two are not nested for these numbers: a share over 100% would mislead).
 */
export function stageConversions(totals: Record<FunnelStage, number> & AccountsWithoutClick): Array<{ from: FunnelStage; to: FunnelStage; pct: number | null }> {
  const out: Array<{ from: FunnelStage; to: FunnelStage; pct: number | null }> = [];
  for (let i = 1; i < FUNNEL_STAGES.length; i += 1) {
    const from = FUNNEL_STAGES[i - 1];
    const to = FUNNEL_STAGES[i];
    const a = Number(totals[from] || 0);
    const b = to === 'accounts'
      ? Math.max(0, Number(totals.accounts || 0) - Number(totals.accounts_without_click || 0))
      : Number(totals[to] || 0);
    out.push({ from, to, pct: a > 0 && b <= a ? Math.round((b / a) * 1000) / 10 : null });
  }
  return out;
}

/** "incl. 4 without a click" under accounts — null when there are none. */
export function accountsWithoutClickText(n: number | null | undefined): string | null {
  const v = Number(n || 0);
  return v > 0 ? `incl. ${v.toLocaleString('en-CA')} without a click` : null;
}

/** Per-tag totals across platforms (the funnel rows are per tag AND platform). */
export function funnelByTag(rows: FunnelRow[]): Array<{ tag: string; platforms: string[] } & Record<FunnelStage, number> & { accrued_cents: number; accounts_without_click: number }> {
  const map = new Map<string, { tag: string; platforms: string[] } & Record<FunnelStage, number> & { accrued_cents: number; accounts_without_click: number }>();
  for (const r of rows) {
    let acc = map.get(r.tag);
    if (!acc) {
      acc = { tag: r.tag, platforms: [], accrued_cents: 0, accounts_without_click: 0 } as never;
      for (const s of FUNNEL_STAGES) (acc as Record<string, number>)[s] = 0;
      map.set(r.tag, acc!);
    }
    acc!.platforms.push(r.platform);
    for (const s of FUNNEL_STAGES) (acc as Record<string, number>)[s] += Number(r[s] || 0);
    acc!.accrued_cents += Number(r.accrued_cents || 0);
    acc!.accounts_without_click += Number(r.accounts_without_click || 0);
  }
  return Array.from(map.values());
}

export const FUNNEL_TOOLTIPS: Partial<Record<FunnelStage | 'ambiguous' | 'accrued' | 'window' | 'accounts_without_click', string>> = {
  installs:
    "Our handoff count: matched iPhone check-ins + Android referrer reads. Apple's campaign report is the cross-check; the difference is the iPhone miss rate for that tag.",
  qualified:
    'Second successful charge only (month-2 renewal or an annual purchase). The first post-trial charge never counts.',
  ambiguous:
    'iPhone first opens where two or more clicks could have matched, so nobody was credited (the strict rule). Platform-wide, not per partner, by construction.',
  accrued: 'Bounties earned in the window, before any statement.',
  window:
    'A lead is counted in the window its CLICK fell in, whatever it did later. An account without a click is counted in the window it was attributed in.',
  accounts_without_click:
    'Accounts this tag brought in with no click on record: an offer code typed in the app, the email match, or a click older than the 13-month retention. Counted in Accounts, on the platform they signed up on; the share of installs counts only accounts that came through a click.',
};

// ── adjustments ──────────────────────────────────────────────────────────────

/** One idempotency key per adjustment form, minted when it opens and resent unchanged on every retry. */
export function newIdempotencyKey(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // RFC 4122 v4 from Math.random — only where crypto.randomUUID is missing (old test runners).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function adjustmentBody(
  form: { amount: string; note: string; tagId: string; userId: string },
  idempotencyKey: string,
  confirmNoBounty = false,
): { body: Record<string, unknown>; errors: string[] } {
  const errors: string[] = [];
  const parsed = parseDollarsToCents(form.amount, { signed: true, nonZero: true });
  if (!parsed.ok) errors.push(`Amount: ${parsed.error}`);
  const note = form.note.trim();
  if (!note) errors.push('A reason is required (it stays internal — statements carry no notes).');
  if (note.length > 500) errors.push('The reason is at most 500 characters.');
  if (form.userId.trim() && !UUID_RE.test(form.userId.trim())) errors.push('User: a user id (UUID), or leave it blank.');
  const body: Record<string, unknown> = {
    amount_cents: parsed.ok ? parsed.cents : null,
    note,
    idempotency_key: idempotencyKey,
  };
  if (form.tagId) body.tag_id = form.tagId;
  if (form.userId.trim()) body.user_id = form.userId.trim();
  if (confirmNoBounty) body.confirm_no_bounty_entry = true;
  return { body, errors };
}

/**
 * "Write it anyway" after a `user_has_no_bounty_entry` refusal: the REFUSED body, byte for
 * byte (same idempotency key, amount, note, tag, user), plus the confirmation. Never rebuilt
 * from the form, which may have changed since the warning was shown.
 */
export function confirmedAdjustmentBody(refused: Record<string, unknown>): Record<string, unknown> {
  return { ...refused, confirm_no_bounty_entry: true };
}

// ── statements: draft + mark paid ────────────────────────────────────────────

/** The month before `today` — what the cron drafts on the 1st. */
export function priorMonth(today: Date): { year: number; month: number } {
  const m = today.getMonth(); // 0-based: the previous month's 1-based number
  return m === 0 ? { year: today.getFullYear() - 1, month: 12 } : { year: today.getFullYear(), month: m };
}

export function markPaidBody(reference: string, paidOn: string): { body: Record<string, unknown>; errors: string[] } {
  const errors: string[] = [];
  const ref = reference.trim();
  if (!ref) errors.push('The payment reference is required (e.g. the e-transfer or cheque number).');
  if (ref.length > 200) errors.push('The reference is at most 200 characters.');
  const body: Record<string, unknown> = { paid_reference: ref };
  if (paidOn.trim()) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(paidOn.trim())) errors.push('Paid on: pick a date.');
    else {
      const [y, m, d] = paidOn.trim().split('-').map(Number);
      body.paid_at = new Date(y, m - 1, d, 12, 0, 0).toISOString();
    }
  }
  return { body, errors };
}

/** A statement note: ≤ 2000, no `<` or `&#` (the PDF reads them as markup); empty clears it. */
export function noteError(note: string): string | null {
  if (note.length > 2000) return 'At most 2,000 characters.';
  if (hasPdfMarkup(note)) return 'Cannot contain "<" or "&#" (the statement PDF reads them as markup).';
  return null;
}

// ── the program banner ───────────────────────────────────────────────────────

export interface BannerLine {
  tone: 'warning' | 'info' | 'error';
  text: string;
}

export function programBanner(status: ProgramStatus | null, loadError: string | null): BannerLine[] {
  if (loadError) {
    return [{ tone: 'error', text: `Could not read the program's switches (${loadError}). Treat the program as dark.` }];
  }
  if (!status) return [];
  const out: BannerLine[] = [];
  if (!status.attribution_enabled) {
    out.push({
      tone: 'warning',
      text:
        'The partner program is DARK (FF_PARTNER_ATTRIBUTION is off): links render but record nothing, nobody is ' +
        'attributed and no bounty accrues. You can set partners, tags, offers and codes up now; statements can still be sent.',
    });
  }
  if (status.payer_copy_enabled && status.payer_identity_gaps.length) {
    out.push({
      tone: 'error',
      text:
        `Approve & send and Resend will be refused: Coach Cedar's payer details are not configured on the server ` +
        `(${status.payer_identity_gaps.join(', ')}). This is a backend setting, not something to fix on this screen.`,
    });
  }
  return out;
}

export function healthNumbersText(numbers: Record<string, number | null>): string {
  return Object.entries(numbers)
    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v ?? '—'}`)
    .join(' · ');
}

export function ledgerTypeLabel(t: LedgerEntry['entry_type']): string {
  return { accrual: 'Accrual', reversal: 'Reversal', payment: 'Payment', adjustment: 'Adjustment' }[t] ?? t;
}

// ── partner API keys (codes platform item C6, migration 1838) ─────────────────

/**
 * The scopes a key can carry. The contacts scopes (partner launch plan E12) hand a key personal
 * data — the partner pushes consented contacts (`contacts:write`) or reads back their state
 * (`contacts:read`) — so they are OPT-IN per key: a new key starts with the codes scopes only.
 */
export const API_KEY_SCOPES = ['codes:mint', 'codes:read', 'stats:read', 'contacts:write', 'contacts:read'] as const;
export type ApiKeyScope = (typeof API_KEY_SCOPES)[number];
export const CONTACT_SCOPES = ['contacts:write', 'contacts:read'] as const;
/** What a new key is created with unless the admin ticks more (services/partner_api.py DEFAULT_SCOPES). */
export const API_KEY_DEFAULT_SCOPES: readonly ApiKeyScope[] = ['codes:mint', 'codes:read', 'stats:read'];
export const API_KEY_SCOPE_LABELS: Record<ApiKeyScope, string> = {
  'codes:mint': 'Mint and revoke codes',
  'codes:read': 'Read tags, offers and codes',
  'stats:read': 'Read aggregate stats',
  'contacts:write': 'Send us consented contacts (personal data)',
  'contacts:read': 'Read back the contacts they sent',
};
export type ApiKeyState = 'active' | 'expiring' | 'expired' | 'revoked';
export const API_KEY_RATE_MAX = 600;
export const API_KEY_QUOTA_MAX = 100000;
/** The daily contact quota's bounds and default (a key with a contacts scope; plan §7). */
export const API_KEY_CONTACT_QUOTA_MAX = 100000;
export const API_KEY_CONTACT_QUOTA_DEFAULT = 10000;
export const PARTNER_API_BASE = 'https://api.coachcedar.com/api/partner/v1';

export function hasContactScope(scopes: readonly string[] | null | undefined): boolean {
  return (scopes ?? []).some((s) => (CONTACT_SCOPES as readonly string[]).includes(s));
}

/** A key as the server lists it: its public prefix, never the key or its hash. */
export interface ApiKeyRow {
  id: string;
  partner_id: string;
  name: string;
  key_prefix: string;
  scopes: ApiKeyScope[];
  rate_limit_per_minute: number;
  daily_mint_quota: number;
  state: ApiKeyState;
  created_by: string;
  created_at: string;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  revoked_by: string | null;
  calls_24h?: number;
  codes_created_24h?: number;
  /** Contacts the key may send per rolling 24 hours (partner launch; null on a key without a contacts scope). */
  daily_contact_quota?: number | null;
}

export interface ApiKeyForm {
  name: string;
  scopes: Record<ApiKeyScope, boolean>;
  rateLimit: string;
  dailyQuota: string;
  /** Sent only when a contacts scope is ticked. */
  dailyContactQuota: string;
}

export function emptyApiKeyForm(): ApiKeyForm {
  const scopes = Object.fromEntries(API_KEY_SCOPES.map((s) => [s, API_KEY_DEFAULT_SCOPES.includes(s)])) as Record<ApiKeyScope, boolean>;
  return { name: '', scopes, rateLimit: '60', dailyQuota: '1000', dailyContactQuota: String(API_KEY_CONTACT_QUOTA_DEFAULT) };
}

/**
 * The create body, checked the way the server (and migration 1838's CHECKs) will. The daily
 * contact quota travels ONLY with a contacts scope: a codes-only key's body is exactly what it
 * was before the contacts scopes existed (the create body model refuses unknown fields).
 */
export function apiKeyBody(form: ApiKeyForm): { body: Record<string, unknown>; errors: string[] } {
  const errors: string[] = [];
  const name = form.name.trim();
  if (!name) errors.push('Name: required (e.g. "Run Club website").');
  else if (name.length > 100) errors.push('Name: at most 100 characters.');
  const scopes = API_KEY_SCOPES.filter((s) => form.scopes[s]);
  if (!scopes.length) errors.push('Scopes: pick at least one.');
  const whole = (text: string, max: number, field: string): number | null => {
    const t = (text ?? '').trim();
    if (!/^\d+$/.test(t) || Number(t) < 1 || Number(t) > max) {
      errors.push(`${field}: a whole number from 1 to ${max.toLocaleString('en-CA')}.`);
      return null;
    }
    return Number(t);
  };
  const rate = whole(form.rateLimit, API_KEY_RATE_MAX, 'Calls per minute');
  const quota = whole(form.dailyQuota, API_KEY_QUOTA_MAX, 'Codes per day');
  const body: Record<string, unknown> = { name, scopes, rate_limit_per_minute: rate, daily_mint_quota: quota };
  if (hasContactScope(scopes)) {
    body.daily_contact_quota = whole(form.dailyContactQuota, API_KEY_CONTACT_QUOTA_MAX, 'Contacts per day');
  }
  return { body, errors };
}

/** The list's "Limits" cell: the contact quota only on a key that carries a contacts scope. */
export function apiKeyLimitsText(k: Pick<ApiKeyRow, 'rate_limit_per_minute' | 'daily_mint_quota' | 'scopes' | 'daily_contact_quota'>): string {
  const base = `${k.rate_limit_per_minute}/min · ${Number(k.daily_mint_quota).toLocaleString('en-CA')} codes/day`;
  if (!hasContactScope(k.scopes) || k.daily_contact_quota == null) return base;
  return `${base} · ${Number(k.daily_contact_quota).toLocaleString('en-CA')} contacts/day`;
}

export function apiKeyStateBadge(state: ApiKeyState): 'success' | 'warning' | 'neutral' | 'error' | 'info' {
  return ({ active: 'success', expiring: 'warning', expired: 'neutral', revoked: 'error' } as const)[state] ?? 'neutral';
}

/** Only a key that still works can be rotated or revoked from the list. */
export function apiKeyActionable(key: Pick<ApiKeyRow, 'state'>): boolean {
  return key.state === 'active' || key.state === 'expiring';
}
